/**
 * Parse Functions for Route Parameters
 *
 * These functions are used to parse URL parameters from deep links.
 * They are extracted into a separate file to avoid circular dependencies
 * between linkingHelpers.ts and routeDefinitions.ts.
 */

type LinkingErrorListener = (message: string | null) => void;

const linkingErrorListeners = new Set<LinkingErrorListener>();

const invalidParamsMessage = 'parametros invalidos';

export const subscribeLinkingErrors = (listener: LinkingErrorListener) => {
  linkingErrorListeners.add(listener);

  return () => {
    linkingErrorListeners.delete(listener);
  };
};

const notifyListeners = (message: string | null) => {
  linkingErrorListeners.forEach((listener) => listener(message));
};

export const emitInvalidParamsError = () => {
  notifyListeners(invalidParamsMessage);
};

export const clearLinkingError = () => {
  notifyListeners(null);
};

export const parseNumberParam = (value: string | undefined) => {
  if (value === undefined) {
    emitInvalidParamsError();
    return undefined;
  }

  const parsed = Number(value);

  if (Number.isNaN(parsed)) {
    emitInvalidParamsError();
    return undefined;
  }

  return parsed;
};

export const parseOptionalNumberParam = (value: string | undefined) => {
  if (value === undefined) {
    return undefined;
  }

  const parsed = Number(value);

  if (Number.isNaN(parsed)) {
    emitInvalidParamsError();
    return undefined;
  }

  return parsed;
};

export const parseStringParam = (value: string | undefined) => {
  if (!value) {
    emitInvalidParamsError();
    return undefined;
  }

  return value;
};

export const parseOptionalStringParam = (value: string | undefined) => {
  if (value === undefined) {
    return undefined;
  }

  if (!value) {
    emitInvalidParamsError();
    return undefined;
  }

  return value;
};

export const parseOptionalJsonParam = <T = unknown>(value: string | undefined): T | undefined => {
  if (value === undefined) {
    return undefined;
  }

  try {
    return JSON.parse(value) as T;
  } catch {
    emitInvalidParamsError();
    return undefined;
  }
};

export const LINKING_INVALID_PARAMS_MESSAGE = invalidParamsMessage;
